<script setup>
import { computed, onMounted, ref, watch } from "vue";
import apiClient from "@/apiClient.js";

const props = defineProps({
  returnId: { type: Number, required: true },
  periodStart: { type: String, required: true },
  periodEnd: { type: String, required: true },
  existingImportId: { type: Number, default: null },
});
const emit = defineEmits(["cancel", "imported"]);
const file = ref(null);
const preview = ref(null);
const matches = ref({});
const loading = ref(false);
const error = ref("");
const notes = ref("");
const showAll = ref(false);
const page = ref(1);
const readonly = computed(() => Boolean(preview.value?.confirmed_at));
const filteredRows = computed(() =>
  (preview.value?.rows ?? []).filter((row) => showAll.value || row.problem),
);
const visibleRows = computed(() =>
  filteredRows.value.slice((page.value - 1) * 50, page.value * 50),
);
watch(
  () => filteredRows.value.length,
  () => {
    page.value = 1;
  },
);
const unresolved = computed(
  () => preview.value?.rows.filter((row) => row.problem).length ?? 0,
);
const matchedCount = computed(
  () => (preview.value?.rows ?? []).filter((row) => !row.problem).length,
);
const issueCount = computed(
  () => unresolved.value + (preview.value?.blocking_issues?.length ?? 0),
);
const canConfirm = computed(
  () => preview.value?.ready && !readonly.value && !loading.value,
);
const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    value / 100,
  );
const usePreview = (data) => {
  preview.value = data;
  matches.value = Object.fromEntries(
    data.rows
      .filter((row) => row.transaction_id)
      .map((row) => [row.row, row.transaction_id]),
  );
};
const fail = (failure) => {
  error.value =
    failure?.response?.data?.message ??
    "Unable to process the historical workbook.";
};
const upload = async () => {
  if (!file.value || loading.value) return;
  loading.value = true;
  error.value = "";
  try {
    const form = new FormData();
    form.append("workbook", file.value);
    const { data } = await apiClient.post(
      `/api/vat-returns/${props.returnId}/imports`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        suppressErrorAlert: true,
      },
    );
    usePreview(data);
    page.value = 1;
  } catch (failure) {
    fail(failure);
  } finally {
    loading.value = false;
  }
};
const refresh = async () => {
  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.post(
      `/api/vat-returns/${props.returnId}/imports/${preview.value?.id ?? props.existingImportId}/preview`,
      { matches: matches.value },
      { suppressErrorAlert: true },
    );
    usePreview(data);
  } catch (failure) {
    fail(failure);
  } finally {
    loading.value = false;
  }
};
const confirm = async () => {
  if (!canConfirm.value) return;
  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.post(
      `/api/vat-returns/${props.returnId}/imports/${preview.value.id}/confirm`,
      {
        matches: matches.value,
        reconciliation_notes: notes.value.trim(),
        filed_records_checked: true,
      },
      { suppressErrorAlert: true },
    );
    emit("imported", data);
  } catch (failure) {
    if (failure?.response?.status === 422) await refresh();
    fail(failure);
  } finally {
    loading.value = false;
  }
};
onMounted(() => {
  if (props.existingImportId) refresh();
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 px-4 py-8"
    role="dialog"
    aria-modal="true"
    aria-labelledby="historical-vat-title"
  >
    <div
      class="max-h-full w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-6 text-slate-900 shadow-2xl dark:bg-slate-800 dark:text-slate-100"
    >
      <div class="flex items-start justify-between gap-4">
        <h2 id="historical-vat-title" class="text-lg font-semibold">
          Historical VAT workbook · {{ periodStart }} – {{ periodEnd }}
        </h2>
        <button
          type="button"
          class="btn"
          :disabled="loading"
          @click="emit('cancel')"
        >
          Close
        </button>
      </div>
      <p class="my-3 text-sm">
        Import a previous Neptune workbook. Expenditure rows match outgoing
        payments (input VAT); income rows match incoming payments (output VAT).
        Original figures and totals are preserved without checking or
        recalculating VAT arithmetic. Clear matches are automatic; only
        exceptions need attention. Confirming ties the matched payments and
        identified invoices to this quarter; closing the quarter is a separate
        step.
      </p>
      <form
        v-if="!existingImportId && !preview"
        class="flex flex-wrap items-end gap-3"
        @submit.prevent="upload"
      >
        <label
          >Neptune .xlsx workbook<input
            type="file"
            accept=".xlsx"
            aria-label="Historical VAT workbook"
            :disabled="loading"
            @change="file = $event.target.files?.[0] ?? null"
        /></label>
        <button class="btn accent" :disabled="!file || loading">
          Preview workbook
        </button>
      </form>
      <p v-if="loading" class="my-3" role="status">
        Checking workbook and allocations…
      </p>
      <p
        v-if="error"
        class="my-3 text-rose-600 dark:text-rose-300"
        role="alert"
      >
        {{ error }}
      </p>
      <template v-if="preview">
        <p class="my-3 text-sm">
          {{ preview.filename }} · {{ preview.rows.length }} rows checked ·
          {{ matchedCount }} matched · {{ issueCount }} issues
        </p>
        <p v-if="preview.summary" class="text-sm">
          {{ preview.summary.input_rows }} input rows ·
          {{ preview.summary.output_rows }} output rows ·
          {{ preview.summary.linked_invoices }} identified invoices to reserve
        </p>
        <div class="my-3 grid gap-3 sm:grid-cols-3">
          <div>
            Workbook input VAT
            <strong class="block">{{ money(preview.input_vat) }}</strong>
          </div>
          <div>
            Workbook output VAT
            <strong class="block">{{ money(preview.output_vat) }}</strong>
          </div>
          <div>
            Workbook liability / repayment
            <strong class="block">{{ money(preview.liability) }}</strong>
          </div>
        </div>
        <p
          v-if="unresolved"
          class="my-3 text-sm text-rose-600 dark:text-rose-300"
          role="status"
        >
          Resolve the {{ unresolved }} exceptions below before confirming.
          Matched rows need no manual review.
        </p>
        <p
          v-else-if="!preview.blocking_issues?.length"
          class="my-3 text-sm"
          role="status"
        >
          All workbook transactions matched. No transaction exceptions need your
          attention.
        </p>
        <p
          v-for="issue in preview.blocking_issues"
          :key="issue"
          role="alert"
          class="my-3 text-sm text-rose-600 dark:text-rose-300"
        >
          {{ issue }}
        </p>
        <p class="my-3 text-sm">
          Both input/outgoing and output/incoming payments were checked. The VAT
          figures are preserved from the workbook.
        </p>
        <p v-if="preview.summary?.rows_without_invoice" class="my-3 text-sm">
          {{ preview.summary.rows_without_invoice }} rows have no invoice record
          linked or identified in Neptune. Their payments can be reserved;
          invoices that do not exist or cannot be identified cannot be locked.
        </p>
        <details
          v-if="preview.summary?.payments_not_in_workbook"
          class="my-3 rounded border border-amber-400 p-3"
        >
          <summary>
            {{ preview.summary.payments_not_in_workbook }} payments in this
            period are outside the workbook
          </summary>
          <p class="my-2 text-sm">
            These payments will not be allocated by this import. This is a
            warning about coverage, rather than a workbook matching failure.
          </p>
          <ul class="text-sm">
            <li
              v-for="item in preview.payments_not_in_workbook"
              :key="`bank-${item.id}`"
            >
              Bank #{{ item.id }} · {{ item.transaction_date }} ·
              {{ item.description }} ·
              {{ money(item.paid_out || item.paid_in) }}
            </li>
            <li
              v-for="item in preview.stripe_payments_not_in_workbook"
              :key="`stripe-${item.id}`"
            >
              Stripe #{{ item.id }} · {{ item.description }} ·
              {{ money(item.gross_amount) }}
            </li>
          </ul>
          <p class="text-xs">Up to 50 payments from each source are shown.</p>
        </details>
        <label class="my-3 flex items-center gap-2 text-sm"
          ><input
            v-model="showAll"
            type="checkbox"
            aria-label="Show all matched rows"
          />
          Show all rows, including automatic matches</label
        >
        <div v-if="filteredRows.length" class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="text-left">
              <tr>
                <th class="p-2">Workbook row</th>
                <th class="p-2">Date / description</th>
                <th class="p-2">Gross / VAT</th>
                <th class="p-2">Existing payment</th>
                <th class="p-2">Result</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleRows"
                :key="row.row"
                class="border-t border-slate-200 dark:border-slate-700"
              >
                <td class="p-2">
                  {{ row.row }} ·
                  {{
                    row.kind === "outgoing"
                      ? "Input / outgoing"
                      : "Output / incoming"
                  }}
                </td>
                <td class="p-2">{{ row.date }}<br />{{ row.description }}</td>
                <td class="p-2 whitespace-nowrap">
                  {{ money(row.gross) }}<br />{{ money(row.vat) }}
                </td>
                <td class="p-2">
                  <template v-if="readonly"
                    >{{ row.source }} #{{ row.transaction_id }}</template
                  >
                  <select
                    v-else-if="row.problem"
                    v-model="matches[row.row]"
                    class="input max-w-sm"
                    :aria-label="`Match workbook row ${row.row}`"
                    :disabled="loading"
                    @change="refresh"
                  >
                    <option :value="null">Choose a payment</option>
                    <option
                      v-for="candidate in row.candidates"
                      :key="candidate.id"
                      :value="candidate.id"
                    >
                      {{ row.source }} #{{ candidate.id }} ·
                      {{ candidate.description }} ·
                      {{ money(candidate.amount) }}
                    </option>
                  </select>
                  <span v-else>{{ row.source }} #{{ row.transaction_id }}</span>
                </td>
                <td
                  class="p-2"
                  :class="row.problem ? 'text-rose-600 dark:text-rose-300' : ''"
                >
                  {{ row.problem || "Matched automatically" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="filteredRows.length > 50"
          class="my-3 flex items-center justify-between"
        >
          <button class="btn" :disabled="page === 1 || loading" @click="page--">
            Previous</button
          ><span
            >Page {{ page }} of {{ Math.ceil(filteredRows.length / 50) }}</span
          ><button
            class="btn"
            :disabled="page * 50 >= filteredRows.length || loading"
            @click="page++"
          >
            Next
          </button>
        </div>
        <div
          v-if="!readonly"
          class="mt-4 space-y-3 rounded border border-amber-400 p-4"
        >
          <label class="block text-sm"
            >Notes (optional)<textarea
              v-model="notes"
              aria-label="Import reconciliation notes"
              class="input mt-1 w-full"
              maxlength="2000"
              :disabled="loading"
              placeholder="Optional notes about exceptions or manual matches."
            />
          </label>
          <p class="text-sm">
            Confirmation reserves the matched payments and identified invoice
            records exclusively for this VAT quarter, preserving the original
            workbook figures. Protected invoice/payment links cannot be
            reassigned.
          </p>
          <button
            type="button"
            class="btn accent"
            :disabled="!canConfirm"
            @click="confirm"
          >
            Confirm historical import
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
